'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import { installErrorHandlers } from '../../src/analytics/error-tracking';
import { bootstrapAgentAppAuth } from '../../src/agent-app-auth';
import { loadAgentAppSession } from '../../src/agent-app-session';
import { installWebObservability } from '../../src/observability/install';

// Install browser exception handlers at module-load time, before any other
// client code can throw. The hooks buffer events until AnalyticsProvider
// finishes `bootstrapExceptionTracking()` with the PostHog key, so even
// errors thrown during the dynamic import of `src/App` are captured.
installErrorHandlers();

// Install the rest of the observability surface (long tasks, white-screen
// detector, resource-error capture, boot timing, visibility tracking).
// Same buffer + consent-bypass transport as the exception handler above
// so events fired before AnalyticsProvider initialises still flush.
installWebObservability();

// The product is a fully client-driven SPA — every component reads
// localStorage, window.location, etc. — so we opt out of static-time
// rendering for the entire tree. This keeps `next build --output export`
// from trying to evaluate browser-only code while still emitting a real
// shell HTML the daemon can serve as the SPA fallback.
const App = dynamic(() => import('../../src/App').then((m) => m.App), {
  ssr: false,
  loading: () => <div className="od-loading-shell">Loading Design App…</div>,
});

export function ClientApp() {
  // Design App fork (per-user isolation): complete the agent-app auth handshake
  // BEFORE mounting the SPA, so the project list/read fetches carry the session
  // cookie and only ever return this user's own designs. Resolves quickly when a
  // launch token is on the URL; falls back to a bounded postMessage handshake
  // when embedded, and renders immediately when opened standalone.
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    bootstrapAgentAppAuth()
      // Warm the session cache so App's useAgentAppSession resolves immediately
      // and paints the workspace brand colour (via open-design's appearance
      // system) on first render — see App.tsx applyAppearanceToDocument.
      .then(() => loadAgentAppSession())
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!authReady) {
    return <div className="od-loading-shell">Loading Design App…</div>;
  }
  return <App />;
}
